/** חוזה ה-API בין ה-web ל-worker. */

export type ApiErrorCode =
  | 'unauthenticated'
  | 'invalid_token'
  | 'forbidden'
  | 'not_found'
  | 'method_not_allowed'
  | 'bad_request'
  | 'conflict'
  | 'already_handled'
  | 'rate_limited'
  | 'server_misconfigured'
  | 'upstream_unavailable'
  | 'internal';

export interface ApiErrorBody {
  error: {
    code: ApiErrorCode;
    message: string;
    /** שניות עד שאפשר לנסות שוב (רק ב-rate_limited) */
    retryAfter?: number;
    /** שגיאות לפי שדה (רק ב-bad_request של טופס) */
    fields?: Record<string, string>;
  };
}

// ── עבודות (שלב 2) ─────────────────────────────────────────────

/** גוף יצירה/עריכה. registrationClosesAt — ISO או null. */
export interface JobWriteRequest {
  name: string;
  date: string;
  reportTime: string;
  location: string;
  notes?: string;
  registrationClosesAt?: string | null;
}

export interface JobUpdateRequest extends JobWriteRequest {
  /** הגרסה שהמנהל ערך — מונעת דריסה של עריכה מקבילה */
  version: number;
}

export interface CreateJobResponse {
  jobId: string;
}

export interface OkResponse {
  ok: true;
}

export type RegistrationAction = 'open' | 'close';
export type AccessRotateTarget = 'code' | 'link';

export interface HealthResponse {
  ok: true;
  service: 'crew-worker';
  version: string;
  time: string;
}

export interface MeResponse {
  uid: string;
  email: string | null;
  displayName: string;
  permissions: readonly string[];
  /** 'owner' = המפתח (רואה את מסך המפתח) */
  role: 'owner' | 'admin';
  quietMode: boolean;
}

/** הודעות שגיאה בעברית שמוצגות למשתמש לפי קוד. */
export const API_ERROR_MESSAGES_HE: Record<ApiErrorCode, string> = {
  unauthenticated: 'נדרשת התחברות',
  invalid_token: 'פג תוקף ההתחברות. יש להתחבר מחדש',
  forbidden: 'לחשבון זה אין הרשאת ניהול',
  not_found: 'הפריט לא נמצא',
  method_not_allowed: 'פעולה לא נתמכת',
  bad_request: 'הנתונים שנשלחו אינם תקינים',
  conflict: 'הנתונים השתנו בינתיים. יש לרענן ולנסות שוב',
  already_handled: 'הבקשה כבר טופלה על ידי מנהל אחר',
  rate_limited: 'יותר מדי ניסיונות. יש לנסות שוב מאוחר יותר',
  server_misconfigured: 'השרת לא הוגדר כראוי. יש לפנות למנהל המערכת',
  upstream_unavailable: 'השירות אינו זמין כרגע. יש לנסות שוב בעוד רגע',
  internal: 'אירעה שגיאה בשרת',
};

// ── קבלנים ועובדים — נקודות ציבוריות (שלב 3) ──────────────────

/** מה שקבלן רואה על העבודה — בלי מזהים פנימיים ובלי פרטי נרשמים אחרים. */
export interface PublicAnnouncement {
  id: string;
  text: string;
  createdAt: string;
  updatedAt: string;
}

export interface PublicJobInfo {
  /** הודעה קבועה מהגדרות המערכת (ריק = בלי) */
  notice?: string;
  /** הכמות המקסימלית לקבלן (מהגדרות המערכת) */
  maxWorkers?: number;
  /** עדכונים שמוצגים גם במסך ההרשמה */
  announcements?: PublicAnnouncement[];
  name: string;
  date: string;
  reportTime: string;
  location: string;
  notes: string;
  registrationOpen: boolean;
  active: boolean;
}

export interface EnterJobRequest {
  code: string;
}

export interface RegisterRequest {
  code: string;
  registrantType: 'contractor' | 'worker';
  fullName: string;
  phone: string;
  workerCount?: number;
}

export interface RegisterResponse {
  personalToken: string;
}

export interface PersonalRequestView {
  id: string;
  previousCount: number;
  requestedCount: number;
  note: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  createdAt: string;
  handledAt: string | null;
}

export interface PersonalViewResponse {
  job: PublicJobInfo;
  registration: {
    registrantType: 'contractor' | 'worker';
    fullName: string;
    phoneMasked: string;
    workerCount: number;
  };
  pendingRequest: PersonalRequestView | null;
  history: PersonalRequestView[];
  /** עדכונים מהמנהל שמיועדים לנרשם הזה */
  announcements: Array<PublicAnnouncement & { isNew: boolean }>;
  chat: { unread: number; closed: boolean };
}

export interface ChatMessageView {
  id: string;
  sender: 'admin' | 'contractor';
  authorName: string;
  text: string;
  createdAt: string;
}

export interface ChatResponse {
  messages: ChatMessageView[];
  closed: boolean;
}

export interface CreateChangeRequest {
  requestedCount: number;
  note?: string;
}

// ── ניהול נרשמים, בקשות, מאגר והתראות (שלבים 3–5) ─────────────

export type RequestDecision = 'approve' | 'reject';

export interface DecideRequestBody {
  decision: RequestDecision;
}

export interface AddRegistrationRequest {
  /** קבלן קיים מהמאגר — או פרטים של חדש */
  contractorId?: string;
  registrantType: 'contractor' | 'worker';
  fullName?: string;
  phone?: string;
  workerCount?: number;
}

export interface PersonalTokenResponse {
  personalToken: string;
}

export interface UpdateRegistrationRequest {
  workerCount: number;
}

export type TokenAction = 'reissue' | 'revoke';

export interface MarkNotificationsReadRequest {
  ids?: string[];
  all?: boolean;
}

export interface RegisterDeviceRequest {
  token: string;
}

// ── עדכונים וצ'אט (מנהל) ──────────────────────────────────────

export interface AnnouncementRequest {
  text: string;
  audience: 'all' | 'contractors' | 'workers' | 'selected';
  recipientIds?: string[];
  showOnRegistration?: boolean;
}

export interface SendMessageRequest {
  text: string;
}

/** עריכת פרטי רשומה במאגר. כל שדה אופציונלי. */
export interface UpdateContractorRequest {
  fullName?: string;
  phone?: string;
}
